import React, { useState } from 'react';
import {
  FormWithRedirect,
  NumberInput,
  SelectInput,
  TextInput,
  useGetOne,
  useNotify,
  useRefresh,
  useTranslate,
  useUpdate,
} from 'react-admin';
import { Field } from 'react-final-form';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import InputMask from 'react-input-mask';
import ModalView from './ModalView';
import { usStateOptions } from '../../UsStates';
// eslint-disable-next-line import/no-cycle
import {
  validateRequiredString,
  validateRequiredYear,
} from '../customer_vehicle';
import CustomActions from './CustomActions';
import DialogTitle from '../../components/DialogTitle';
import ImageUploadSelector from '../ImageUploadSelector';

const useStyles = makeStyles(() => ({
  editVehicleField: {
    margin: '0px',
    width: '100%',
  },
}));

const validateTireSizeField = (value) =>
  value && value.includes('_')
    ? 'Tire size must contain 10 characters'
    : undefined;

const TireSizeFieldAdapter = ({ input, meta, label, ...rest }) => (
  <>
    <InputMask
      {...input}
      {...rest}
      mask="P999/99R99"
      onChange={(event) =>
        input.onChange(
          event.target.value === 'P___/__R__' ? null : event.target.value,
        )
      }
    >
      {() => (
        <TextField
          variant="filled"
          fullWidth
          label={label}
          placeholder="P___/__R__"
        />
      )}
    </InputMask>
    {meta.error && meta.touched && (
      <>
        <br /> <span style={{ color: 'red' }}>{meta.error}</span>
      </>
    )}
  </>
);

// Component used for editing tire size fields, such as 'selected_tire_size'
const TireSizeEdit = ({ name, translate }) => (
  <Typography variant="body1">
    <Field
      label={translate(`resources.vehicle.fields.${name}`)}
      name={name}
      component={TireSizeFieldAdapter}
      validate={validateTireSizeField}
    />
  </Typography>
);

const VehicleEdit = (props) => {
  const classes = useStyles();
  const { openEdit, setOpenEdit, id } = props;
  const { data: vehicle } = useGetOne('vehicle', id);
  const [update, { loading }] = useUpdate('vehicle');
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);

  const handleSubmit = async (values) => {
    update(
      { payload: { id, data: values, previousData: vehicle } },
      {
        onSuccess: () => {
          setOpenEdit(false);
          notify('Vehicle updated successfully');
          refresh();
        },
        onFailure: ({ error }) => {
          notify(error.message, 'error');
        },
      },
    );
  };
  return (
    <ModalView openEdit={openEdit} setOpenEdit={setOpenEdit}>
      <DialogTitle onClose={() => setOpenEdit(false)}>Edit Vehicle</DialogTitle>
      <FormWithRedirect
        resource="vehicle"
        record={vehicle}
        save={handleSubmit}
        render={(formProps) => (
          <>
            <DialogContent>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <NumberInput
                      validate={
                        vehicle?.input_method_type === 'heavy'
                          ? []
                          : validateRequiredYear
                      }
                      source="year"
                      className={classes.editVehicleField}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <TextInput
                      validate={validateRequiredString}
                      source="make"
                      className={classes.editVehicleField}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <TextInput
                      validate={
                        vehicle?.input_method_type === 'heavy'
                          ? []
                          : validateRequiredString
                      }
                      source="model"
                      className={classes.editVehicleField}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <TextInput
                      source="color"
                      className={classes.editVehicleField}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <SelectInput
                      source="license_plate_state"
                      choices={usStateOptions}
                      className={classes.editVehicleField}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <TextInput
                      source="license_plate_number"
                      className={classes.editVehicleField}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <Box>
                    <TextInput source="vin" fullWidth />
                  </Box>
                </Grid>

                {vehicle?.input_method_type === 'heavy' && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <TextInput
                        source="heavy_duty_engine_make"
                        className={classes.editVehicleField}
                        label={translate(
                          'resources.vehicle.fields.heavy_duty_engine_make',
                        )}
                      />
                    </Box>
                  </Grid>
                )}

                {vehicle?.input_method_type === 'heavy' && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <TextInput
                        source="heavy_duty_engine_serial_number"
                        className={classes.editVehicleField}
                        label={translate(
                          'resources.vehicle.fields.heavy_duty_engine_serial_number',
                        )}
                      />
                    </Box>
                  </Grid>
                )}

                {vehicle?.input_method_type === 'heavy' && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <TextInput
                        source="fuel_type"
                        className={classes.editVehicleField}
                        label={translate('resources.vehicle.fields.fuel_type')}
                      />
                    </Box>
                  </Grid>
                )}

                {!!vehicle?.standard_tire_sizes?.length && (
                  <Grid item xs={12} sm={12}>
                    <Box>
                      <Typography variant="body1">
                        <strong>
                          {`${translate(
                            `resources.vehicle.fields.standard_tire_sizes`,
                          )}: `}
                        </strong>
                        {vehicle?.standard_tire_sizes?.join(', ')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body1">
                      <TireSizeEdit
                        name="selected_tire_size"
                        translate={translate}
                      />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body1">
                      <TireSizeEdit
                        name="technician_verified_tire_size"
                        translate={translate}
                      />
                    </Typography>
                  </Box>
                </Grid>
                {vehicle?.input_method_type === 'heavy' && (
                  <Grid item xs={12}>
                    <Box>
                      <ImageUploadSelector
                        record={vehicle || {}}
                        resource="vehicle"
                        source="photo_filenames"
                        folder="vehicle"
                        setIsUploading={setIsPhotoUploading}
                        multiple
                        saveAsUrl={false}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <CustomActions
              {...formProps}
              setOpenEdit={setOpenEdit}
              loading={loading || isPhotoUploading}
            />
          </>
        )}
      />
    </ModalView>
  );
};

export default VehicleEdit;
